"use client";
import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestHeaders,
} from "axios";
import { signOut } from "next-auth/react";
import { toast } from "react-toastify";
import type { Establishment } from "@/types";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
  }
}

interface TokenContextValue {
  token: string | null;
  establishment: Establishment | null;
  baseURL: string | null;
}

let globalTokenData: TokenContextValue = {
  token: null,
  establishment: null,
  baseURL: null,
};

let isRedirecting = false;

const axiosInstance = axios.create({
  timeout: 30000,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  headers: {
    "Content-Type": "application/json",
  },
  maxRedirects: 5,
});

export const setAxiosTokenData = (tokenData: TokenContextValue) => {
  globalTokenData = tokenData;

  if (tokenData.baseURL) {
    axiosInstance.defaults.baseURL = tokenData.baseURL;
  }

  if (tokenData.token) {
    axiosInstance.defaults.headers.common["Authorization"] =
      `Bearer ${tokenData.token}`;
  } else {
    delete axiosInstance.defaults.headers.common["Authorization"];
  }
};

export const clearAxiosTokenData = () => {
  globalTokenData = {
    token: null,
    establishment: null,
    baseURL: null,
  };

  delete axiosInstance.defaults.headers.common["Authorization"];
  axiosInstance.defaults.baseURL = process.env.NEXT_PUBLIC_API_URL;
};

const addEstablishmentParams = (config: InternalAxiosRequestConfig) => {
  const url = config.url || "";
  const isAdminEndpoint = url.startsWith("/admin/");
  if (isAdminEndpoint) {
    return config;
  }
  const excludedEndpoints = [
    "/user-service-commission",
    "/subscriptions/config",
    "/billing/",
    "/billing",
  ];

  const shouldExclude = excludedEndpoints.some(
    (endpoint) => config.url && config.url.includes(endpoint),
  );

  if (globalTokenData.establishment && !shouldExclude) {
    config.params = {
      ...config.params,
      filter: {
        ...config.params?.filter,
        establishment_id: globalTokenData.establishment.id,
      },
    };
  }

  return config;
};

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const url = config.url || "";
    if (url.startsWith("/admin/")) {
      config.baseURL = process.env.NEXT_PUBLIC_API_URL;
    }

    const isFormData =
      typeof FormData !== "undefined" && config.data instanceof FormData;
    if (isFormData) {
      if ((config.headers as AxiosRequestHeaders)["Content-Type"]) {
        delete (config.headers as AxiosRequestHeaders)["Content-Type"];
      }
    }

    config = addEstablishmentParams(config);

    config.metadata = { startTime: Date.now() };

    // Debug: log outgoing admin requests for visibility
    try {
      // eslint-disable-next-line no-console
      console.debug("axios.request", {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
        params: config.params,
      });
    } catch (e) {
      /* ignore */
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (
      process.env.NODE_ENV === "development" &&
      response.config.metadata?.startTime
    ) {
      const duration = Date.now() - response.config.metadata.startTime;
      const url = response.config.url;
      if (duration > 3000) {
        console.warn(`🐌 Requisição lenta: ${url} - ${duration}ms`);
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      if (typeof window !== "undefined" && !isRedirecting) {
        isRedirecting = true;

        clearAxiosTokenData();

        toast.warning(
          "Sua sessão expirou. Você será redirecionado para a página de login.",
        );

        setTimeout(async () => {
          await signOut({
            redirect: true,
            callbackUrl: "/login",
          });
        }, 2000);
      }
    }

    return Promise.reject(error);
  },
);

export const isAxiosError = (error: unknown): error is AxiosError =>
  axios.isAxiosError(error);

export default axiosInstance;
